import * as React from "react";
import { useEffect, useCallback } from "react";
import { Dropdown } from "react-bootstrap";

// Types
interface Application {
  id: number;
  status?: string;
  [key: string]: any;
}

interface ApplicationStatusProps {
  application: Application;
  updateStatusFunc: (application: Application, newStatus: string) => void;
  className?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outline' | 'minimal';
  /** 'select' renders the classic form-select; 'menu' renders a compact
   *  status chip that opens a context-menu (for tight layouts like the
   *  mobile applicant cards). */
  display?: 'select' | 'menu';
  showLabel?: boolean;
  allowEmpty?: boolean;
  customStatuses?: string[];
  onStatusChange?: (oldStatus: string, newStatus: string, application: Application) => void;
}

interface ApplicationStatusState {
  application: Application;
  selection: string;
  isLoading?: boolean;
  error?: string;
}

// Constants
export const APPLICATION_STATUSES = [
  'Contacted',
  'Interview Scheduled', 
  'Interview Completed',
  'Hired',
  'Passed',
  'Not Qualified',
  'Not Interested',
  'Candidate Cancelled',
  'Candidate No-Show'
] as const;

export type ApplicationStatusType = typeof APPLICATION_STATUSES[number];

// Legacy export for backward compatibility
export const selections = APPLICATION_STATUSES;

const STATUS_CONFIG = {
  'Contacted': { color: 'info', icon: 'fas fa-phone' },
  'Interview Scheduled': { color: 'warning', icon: 'fas fa-calendar-check' },
  'Interview Completed': { color: 'primary', icon: 'fas fa-handshake' },
  'Hired': { color: 'success', icon: 'fas fa-trophy' },
  'Passed': { color: 'secondary', icon: 'fas fa-times-circle' },
  'Not Qualified': { color: 'secondary', icon: 'fas fa-thumbs-down' },
  'Not Interested': { color: 'secondary', icon: 'fas fa-ban' },
  'Candidate Cancelled': { color: 'warning', icon: 'fas fa-user-times' },
  'Candidate No-Show': { color: 'danger', icon: 'fas fa-user-slash' }
} as const;

const SIZE_CLASSES = {
  sm: 'form-select-sm',
  md: '',
  lg: 'form-select-lg'
} as const;

// Utility Functions
const createInitialState = (application: Application): ApplicationStatusState => ({
  application,
  selection: application.status || '',
  isLoading: false,
  error: undefined
});

const sendApiRequest = async (application: Application, status: string): Promise<void> => {
  const url = `/jobs/update_app/${application.id}?value=${encodeURIComponent(status)}`;
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    await response.json();
  } catch (error) {
    console.error('Failed to update application status:', error);
    throw error;
  }
};

// Sub-components
const StatusIcon: React.FC<{ status: string; className?: string }> = React.memo(({ status, className = "" }) => {
  const config = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG];
  
  if (!config) return null;
  
  return (
    <i 
      className={`${config.icon} ${className}`} 
      aria-hidden="true"
      title={status}
    />
  );
});

StatusIcon.displayName = 'StatusIcon';

const StatusBadge: React.FC<{ status: string; showIcon?: boolean }> = React.memo(({ status, showIcon = true }) => {
  const config = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG];
  
  if (!status || !config) {
    return (
      <span className="badge bg-light text-dark">
        <i className="fas fa-question-circle me-1" aria-hidden="true"></i>
        No Status
      </span>
    );
  }
  
  return (
    <span className={`badge bg-${config.color}`}>
      {showIcon && <StatusIcon status={status} className="me-1" />}
      {status}
    </span>
  );
});

StatusBadge.displayName = 'StatusBadge';

/**
 * ApplicationStatus Component
 * 
 * A comprehensive application status management component with dropdown selection.
 * Handles status updates via API calls and provides visual feedback for different states.
 * 
 * Features:
 * - Status dropdown with predefined options
 * - API integration for status updates
 * - Multiple display variants and sizes
 * - Loading states and error handling
 * - Status icons and color coding
 * - Custom status support
 * - TypeScript safety
 * 
 * Use Cases:
 * - Job application management
 * - Candidate tracking workflows
 * - Interview process management
 * - Hiring pipeline visualization
 */
const ApplicationStatus: React.FC<ApplicationStatusProps> = React.memo(({
  application,
  updateStatusFunc,
  className = "",
  disabled = false,
  size = 'sm',
  variant = 'default',
  display = 'select',
  showLabel = false,
  allowEmpty = true,
  customStatuses,
  onStatusChange
}) => {
  const [state, setState] = React.useState<ApplicationStatusState>(() =>
    createInitialState(application)
  );

  const statuses = customStatuses || APPLICATION_STATUSES;

  // Update local state when application prop changes
  useEffect(() => {
    if (application.status !== state.selection) {
      setState(prevState => ({
        ...prevState,
        application,
        selection: application.status || '',
        error: undefined
      }));
    }
  }, [application.status, state.selection]);

  // Handle status update (shared by the select and the menu display)
  const commitStatus = useCallback(async (newStatus: string) => {
    const oldStatus = state.selection;

    // Optimistic update
    setState(prevState => ({
      ...prevState,
      selection: newStatus,
      isLoading: true,
      error: undefined
    }));

    // Call parent update function immediately
    updateStatusFunc(application, newStatus);
    
    // Trigger custom callback
    if (onStatusChange) {
      onStatusChange(oldStatus, newStatus, application);
    }

    try {
      await sendApiRequest(application, newStatus);
      setState(prevState => ({
        ...prevState,
        isLoading: false
      }));
    } catch (error) {
      // Revert on error
      setState(prevState => ({
        ...prevState,
        selection: oldStatus,
        isLoading: false,
        error: 'Failed to update status'
      }));
      
      // Revert parent state
      updateStatusFunc(application, oldStatus);
      
      console.error('ApplicationStatus update failed:', error);
    }
  }, [application, state.selection, updateStatusFunc, onStatusChange]);

  const updateStatus = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => commitStatus(e.target.value),
    [commitStatus]
  );

  // Get select classes based on variant and size
  const getSelectClasses = () => {
    const baseClasses = ['form-select', SIZE_CLASSES[size]];
    
    if (variant === 'outline') {
      baseClasses.push('border-2');
    } else if (variant === 'minimal') {
      baseClasses.push('border-0', 'bg-light');
    }
    
    if (state.isLoading) {
      baseClasses.push('opacity-75');
    }
    
    if (state.error) {
      baseClasses.push('is-invalid');
    }
    
    return baseClasses.join(' ');
  };

  if (display === 'menu') {
    const current = state.selection;
    const currentConfig = STATUS_CONFIG[current as keyof typeof STATUS_CONFIG];
    return (
      <div className={`application-status-container ${className}`} style={{ margin: 0 }}>
        <Dropdown align="end">
          <Dropdown.Toggle
            as="button"
            type="button"
            className={`status-chip${current ? ' status-chip--set' : ''}`}
            disabled={disabled || state.isLoading}
            aria-label={current ? `Status: ${current}` : 'Set status'}
          >
            {state.isLoading ? (
              <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
            ) : currentConfig ? (
              <StatusIcon status={current} />
            ) : null}
            <span className="status-chip__label">{current || 'Set Status'}</span>
            <i className="fas fa-chevron-down status-chip__chevron" aria-hidden="true"></i>
          </Dropdown.Toggle>
          <Dropdown.Menu className="context-menu context-menu--scroll">
            {statuses.map(status => (
              <Dropdown.Item
                key={status}
                as="button"
                type="button"
                className={`context-menu__item${status === current ? ' context-menu__item--active' : ''}`}
                onClick={() => status !== current && commitStatus(status)}
              >
                <StatusIcon status={status} />
                {status}
                {status === current && (
                  <i className="fas fa-check context-menu__check" aria-hidden="true"></i>
                )}
              </Dropdown.Item>
            ))}
            {allowEmpty && current && (
              <>
                <Dropdown.Divider />
                <Dropdown.Item
                  as="button"
                  type="button"
                  className="context-menu__item"
                  onClick={() => commitStatus('')}
                >
                  <i className="fas fa-eraser" aria-hidden="true"></i>
                  Clear Status
                </Dropdown.Item>
              </>
            )}
          </Dropdown.Menu>
        </Dropdown>
        {state.error && (
          <div className="text-danger small mt-1">
            <i className="fas fa-exclamation-triangle me-1" aria-hidden="true"></i>
            {state.error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`application-status-container ${className}`} style={{ width: '100%', margin: 0 }}>
      {showLabel && (
        <label className="form-label small text-muted mb-1">
          <i className="fas fa-tasks me-1" aria-hidden="true"></i>
          Application Status
        </label>
      )}

      <div className="position-relative">
        <select
          onChange={updateStatus}
          className={getSelectClasses()}
          aria-label="Application status"
          value={state.selection}
          disabled={disabled || state.isLoading}
          style={{ marginBottom: 0 }}
        >
          {allowEmpty && (
            <option value="">
              {state.isLoading ? 'Updating...' : 'Select Status'}
            </option>
          )}
          {statuses.map(status => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>

        {state.isLoading && (
          <div
            className="position-absolute top-50 end-0 translate-middle-y me-3"
            style={{ pointerEvents: 'none' }}
          >
            <div
              className="spinner-border spinner-border-sm text-primary"
              role="status"
              style={{ width: '1rem', height: '1rem' }}
            >
              <span className="visually-hidden">Updating...</span>
            </div>
          </div>
        )}
      </div>
      
      {state.error && (
        <div className="invalid-feedback d-block">
          <i className="fas fa-exclamation-triangle me-1" aria-hidden="true"></i>
          {state.error}
        </div>
      )}
    </div>
  );
});

ApplicationStatus.displayName = 'ApplicationStatus';

export default ApplicationStatus;
export { StatusIcon, StatusBadge };
export type { ApplicationStatusProps, Application };
